import { type LinkProps, Link as MuiLink } from '@mui/material';
import NextLink from 'next/link';
import React from 'react';

export type ModerneLinkProps = {
  href: string;
  linkText?: string;
  external?: boolean;
  prefetch?: boolean;
} & LinkProps;

/**
 *
 * @param {string} linkText - Will take precedence over any `children` passed
 * @returns {ReactNode} - Internal NextJS or external link
 */
export const Link = React.forwardRef<HTMLAnchorElement, ModerneLinkProps>(
  (
    {
      href,
      linkText = undefined,
      external = false,
      prefetch,
      children,
      ...linkProps
    },
    ref
  ) => {
    // Determine if link opens in new tab
    const opensInNewTab = external || linkProps.target === '_blank';

    // Automatically add security attributes for links that open in new tabs
    const securityRel = opensInNewTab ? 'noopener noreferrer' : undefined;

    // Merge rel attributes if user provided one
    const finalRel = linkProps.rel
      ? `${linkProps.rel} ${securityRel || ''}`.trim()
      : securityRel;

    return (
      <MuiLink
        href={href}
        component={opensInNewTab ? undefined : NextLink}
        prefetch={opensInNewTab ? undefined : prefetch}
        target={opensInNewTab ? '_blank' : linkProps.target}
        rel={finalRel}
        underline="hover"
        {...linkProps}
        ref={ref}
        sx={[
          {
            color: 'primary',
            // MUI drives the underline off this CSS variable
            // independently of `color`, which causes underlines to
            // diverge from the text color when consumers override `sx`
            // — pin it to currentColor so the underline always tracks.
            '--Link-underlineColor': 'currentColor'
          },
          ...(Array.isArray(linkProps.sx) ? linkProps.sx : [linkProps.sx])
        ]}
      >
        {linkText || children}
      </MuiLink>
    );
  }
);

type OptionalLinkProps = Omit<ModerneLinkProps, 'href'> & { href?: string };

export const OptionalLink: React.FunctionComponent<OptionalLinkProps> = ({
  href,
  children,
  ...linkProps
}) =>
  href ? (
    <Link href={href} {...linkProps}>
      {children}
    </Link>
  ) : (
    children
  );
